import { useCallback, useEffect, useRef, useState } from 'react';
import { getStatusBucket } from '../utils/status';

// How often to poll the API for pending translations while the tab is visible.
// Long enough that a few in-flight requests don't hammer the gateway, short
// enough that the user sees the result soon after it lands.
const POLL_INTERVAL_MS = 8000;

// Tracks translation requests the user submitted in this session and polls
// the API for their status until each one completes (or errors). Polling is
// "visibility-aware": the timer only runs while document.visibilityState is
// 'visible', so a tab left in the background does not generate calls.
//
// Callers wire it up by passing getStatus (an API call) and the onReady /
// onError side-effects (typically a toast notification + grid refresh).
export default function usePendingTranslations({ getStatus, onReady, onError }) {
  const [pending, setPending] = useState([]);

  // Keep a ref in sync so the polling callback always sees the freshest list
  // without forcing the polling effect to tear down on every state change.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const getStatusRef = useRef(getStatus);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { getStatusRef.current = getStatus; }, [getStatus]);

  const trackSubmission = useCallback((reqId, source) => {
    if (!reqId) return;
    setPending((prev) => {
      if (prev.some((p) => p.reqId === reqId)) return prev;
      return [...prev, { reqId, source: source || '', submittedAt: Date.now() }];
    });
  }, []);

  const pollOnce = useCallback(async () => {
    const list = pendingRef.current;
    if (list.length === 0) return;

    for (const item of list) {
      try {
        const result = await getStatusRef.current(item.reqId);
        const bucket = getStatusBucket(result && result.status);

        if (bucket === 'translated' || bucket === 'approved') {
          setPending((prev) => prev.filter((p) => p.reqId !== item.reqId));
          if (onReadyRef.current) {
            onReadyRef.current({
              ...item,
              status: result.status,
              target: result.target,
            });
          }
        } else if (bucket === 'error') {
          setPending((prev) => prev.filter((p) => p.reqId !== item.reqId));
          if (onErrorRef.current) {
            onErrorRef.current({
              ...item,
              status: result.status,
              error_message: result.message || result.error_message,
            });
          }
        }
      } catch (err) {
        // Network blips are ignored; the next tick will retry.
        // We deliberately do not surface these as errors to avoid spam.
        // eslint-disable-next-line no-console
        console.warn('Pending translation poll failed:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (pending.length === 0) return undefined;

    let intervalId = null;
    let cancelled = false;

    const start = () => {
      if (intervalId) return;
      // Run an immediate check so the user sees fast results in the common
      // case where the translation finished while the tab was hidden.
      pollOnce();
      intervalId = setInterval(() => {
        if (!cancelled) pollOnce();
      }, POLL_INTERVAL_MS);
    };

    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && pendingRef.current.length > 0) {
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') {
      start();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [pending.length, pollOnce]);

  return { trackSubmission, pendingCount: pending.length };
}
